/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {EnvironmentService} from "@irian-ro/util-environment";
import {Observable} from 'rxjs';
import {LazyLoadEvent} from "primeng/api";
import { AbstractResourceService, AdditionalParams, JsonApiListResourceMetadataBase, JsonApiResource, QueryParams } from '@irian-ro/abstract-resource';
import { EmailablePayloadWithContactsMapDTO, ExternalInvoiceOrderListVoModel, InvoiceAgreementInfoModel, InvoiceAttachmentSaveLocationVoModel, InvoiceCancelSelectVo, InvoiceDTO, InvoiceExternalDtoModel, InvoiceExternalMetaModel, InvoiceListMeta, InvoiceListVO, InvoiceMeta, InvoiceStatusDto } from '@well/domain';

@Injectable({
  providedIn: 'root'
})
export class InvoiceResourceService extends AbstractResourceService<InvoiceListVO, InvoiceDTO, InvoiceListMeta, InvoiceMeta> {

  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('invoiceUrl'),
      InvoiceListVO,
      InvoiceDTO
    );
  }

  getInvoiceTemplate(resource): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return this.httpClient?.post(`${this.url}/generate`, resource, {headers: this.headers})
      .pipe(this.getDtoMapper());
  }

  recalculate(invoice: InvoiceDTO): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.put(`${this.url}/recalculate`, invoice, {headers: this.headers});
  }

  getInvoice(uuid): Observable<InvoiceDTO> {
    return this.httpClient?.get(`${this.url}/details/${uuid}`)
      .pipe(this.getDtoMapper());
  }

  markAsPaid(uuid): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.put(`${this.url}/mark/paid/${uuid}`, null, {headers: this.headers});
  }

  markAsCustomPayment(customPaymentAgreement: InvoiceAgreementInfoModel): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.put(`${this.url}/mark/customPayment/${customPaymentAgreement.invoiceUuid}`, customPaymentAgreement, {headers: this.headers});
  }

  cancelInvoice(uuid): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> >this.httpClient?.post(`${this.url}/cancel/${uuid}`, null, {headers: this.headers});
  }

  markAsPending(uuid: string): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return this.httpClient?.post(`${this.url}/mark-as-pending`, uuid, {headers: this.headers})
      .pipe(this.getDtoMapper());
  }

  markAsFinalized(uuid: string): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.post(`${this.url}/${uuid}/mark-as-finalized`, {});
  }

  markAsRejected(uuid: string, invoiceStatusDto: InvoiceStatusDto): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.post(`${this.url}/${uuid}/mark-as-rejected`, invoiceStatusDto);
  }

  recalculateAndMarkAsPending(invoice: InvoiceDTO): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return this.httpClient?.put(`${this.url}/recalculate-and-mark-as-pending`, invoice, {headers: this.headers})
      .pipe(this.getDtoMapper());
  }

  sendByEmail(uuid: string) {
    return this.httpClient?.post(`${this.url}/email/send`, uuid, {headers: this.headers});
  }

  saveExternalIssuedInvoice(eexternalInvoiceWithContacts: EmailablePayloadWithContactsMapDTO<InvoiceExternalDtoModel>): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>> this.httpClient?.post(`${this.url}/external`, eexternalInvoiceWithContacts);
  }

  getExternalIssuedInvoiceTemplate(): Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> {
    return <Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>>>this.httpClient?.get(`${this.url}/external/template`);
  }

  getExternalIssuedInvoiceTemplateByOrderUuid(orderUuid: string) : Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> {
    return <Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>>>this.httpClient?.get(`${this.url}/external/template/${orderUuid}`);
  }

  getExternalInvoiceableOrderReceivedFromCustomer(customerUuid: string, currencyCode: string, includeOnlyNotGeneratedInvoices = true): Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>> {
    return <Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/external/orders/${customerUuid}`, {
      params: {
        currencyCode: currencyCode,
        includeOnlyNotGeneratedInvoices: includeOnlyNotGeneratedInvoices
      }
    });
  }

  getExternalInvoiceableOrderReceivedFromCustomerForInvoice(customerUuid: string, invoiceUuid: string, currencyCode: string, includeOnlyNotGeneratedInvoices = true): Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>{
    return <Observable<JsonApiResource<ExternalInvoiceOrderListVoModel[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/external/orders/${customerUuid}/invoice/${invoiceUuid}`, {
      params: {
        currencyCode: currencyCode,
        includeOnlyNotGeneratedInvoices: includeOnlyNotGeneratedInvoices
      }
    });
  }

  getExternalIssuedInvoiceDetails(uuid: string): Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> {
    return <Observable<JsonApiResource<InvoiceExternalDtoModel, InvoiceExternalMetaModel>> >this.httpClient?.get(`${this.url}/external/details/${uuid}`);
  }

  updateExternalIssuedInvoice(externalInvoiceWithContacts: EmailablePayloadWithContactsMapDTO<InvoiceExternalDtoModel>): Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>> {
    return <Observable<JsonApiResource<InvoiceDTO, InvoiceMeta>>>this.httpClient?.put(`${this.url}/external`, externalInvoiceWithContacts);
  }

  getCancelableIssuedInvoices(customerUuid: string, event: LazyLoadEvent, additionalParams?: Array<AdditionalParams>): Observable<JsonApiResource<InvoiceCancelSelectVo[], JsonApiListResourceMetadataBase>>{
    const queryString = new QueryParams(event, additionalParams ?? []);

    return <Observable<JsonApiResource<InvoiceCancelSelectVo[], JsonApiListResourceMetadataBase>>>this.httpClient?.get(`${this.url}/cancelable/${customerUuid}${queryString}`);
  }

  resendInvoiceUploadInvitationEmail(rowDataUuid: string) {
    return <Observable<unknown>>this.httpClient?.post(`${this.url}/upload/email/order/${rowDataUuid}`, null);
  }

  getCurrentInvoiceAttachment(orderUuid: string) {
    return <Observable<JsonApiResource<InvoiceAttachmentSaveLocationVoModel, InvoiceMeta>>>this.httpClient?.get(`${this.url}/attachment/order/${orderUuid}`);
  }

  downloadInvoicesAttachmentsZipFor(orderUuid: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
    });
    return this.httpClient?.get(`${this.url}/download-all/order/${orderUuid}`, {
      headers,
      responseType: 'blob',
      observe: 'response',
    }).subscribe((res: any) => {
      const contentDisposition = res.headers.get('content-disposition');
      const fileName = contentDisposition
        ? contentDisposition.replace('attachment; filename=', '').replace(/"/g, '')
        : null;

      const link = document.createElement('a');
      link.href = res.body ? window.URL?.createObjectURL(res.body) : '';
      link.download = fileName ? fileName : '';
      link.click();
    });
  }
}
